/**
 * Polygon Drawing Controller - handles polygon drawing operations
 * Manages polyline drawing mode on the map
 */
import {Subject} from 'rxjs';
import {calculateAreaHa} from './drawing-calculations.util';
import {BaseDrawController} from './base-draw.controller';
import {TranslateService} from '@ngx-translate/core';

export class DrawPolygonController extends BaseDrawController {
  private polylineDrawingEnabled = false;
  private currentPolylinePoints: any[] = [];
  private currentPolylineMarkers: any[] = [];
  private currentPolyline: any = null;

  // Track completed polygons
  private completedPolygons: any[] = [];

  // Event handlers stored for cleanup
  private dblClickHandler: any;
  private contextMenuHandler: any;
  private keyDownHandler: any;
  private drawingControlsContainer: any;

  // Real-time measurement labels
  private measurementLabels: any[] = [];
  private areaLabel: any = null;
  private perimeterLabel: any = null;

  // Event emitter for shape creation
  public shapeCreated$ = new Subject<{
    type: 'polygon';
    coordinates: [number, number][];
    center: { lat: number; lng: number };
    layer: any;
  }>();

  constructor(mapInstance: any, private translateService: TranslateService) {
    super(mapInstance);
  }

  /**
   * Enable polygon drawing mode with enhanced interaction
   */
  public enable(): void {
    if (this.polylineDrawingEnabled) return;

    this.polylineDrawingEnabled = true;
    this.enableDrawingMode();
    (this.map as any).doubleClickZoom.disable();

    // Add drawing control buttons
    this.addDrawingControls();

    // Attach event listeners
    this.mapClickHandler = (e: any) => this.handlePolylineClick(e);
    this.mapMouseMoveHandler = (e: any) => this.handlePolylineMouseMove(e);
    this.dblClickHandler = (e: any) => {
      (window as any).L.DomEvent.stopPropagation(e);
      (window as any).L.DomEvent.preventDefault(e);
    };
    this.contextMenuHandler = (e: any) => this.handlePolylineRightClick(e);
    this.keyDownHandler = (e: any) => this.handlePolylineKeyDown(e);

    this.map.on('click', this.mapClickHandler);
    this.map.on('mousemove', this.mapMouseMoveHandler);
    this.map.on('dblclick', this.dblClickHandler);
    this.map.getContainer().addEventListener('contextmenu', this.contextMenuHandler);
    document.addEventListener('keydown', this.keyDownHandler);
  }

  /**
   * Disable polygon drawing mode
   */
  public disable(): void {
    this.polylineDrawingEnabled = false;
    this.cancelCurrentPolyline();
    this.removeDrawingControls();
    this.disableDrawingMode();
    (this.map as any).doubleClickZoom.enable();

    // Remove event listeners
    this.removeMapEventListeners([
      { event: 'click', handler: this.mapClickHandler },
      { event: 'mousemove', handler: this.mapMouseMoveHandler },
      { event: 'dblclick', handler: this.dblClickHandler }
    ]);

    this.removeDomEventListeners([
      { element: this.map.getContainer(), event: 'contextmenu', handler: this.contextMenuHandler },
      { element: document, event: 'keydown', handler: this.keyDownHandler }
    ]);

    this.mapClickHandler = null;
    this.mapMouseMoveHandler = null;
    this.dblClickHandler = null;
    this.contextMenuHandler = null;
    this.keyDownHandler = null;

    // Clear completed polygons
    this.clearCompletedPolygons();
  }

  /**
   * Check if polygon drawing is active
   */
  public override isActive(): boolean {
    return this.polylineDrawingEnabled;
  }

  /**
   * Add drawing control buttons (Undo and Cancel)
   */
  private addDrawingControls(): void {
    // Create control container
    const controlContainer = document.createElement('div');
    controlContainer.className = 'leaflet-drawing-controls';
    controlContainer.style.cssText = `
      position: absolute;
      top: 80px;
      left: 10px;
      z-index: 1000;
      display: none;
      flex-direction: column;
      gap: 8px;
    `;

    // Create Undo button
    const undoButton = document.createElement('button');
    undoButton.className = 'leaflet-control-button drawing-undo-btn';
    undoButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M9 14L4 9l5-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M20 20v-7a4 4 0 0 0-4-4H4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    undoButton.title = 'Undo Last Point (Ctrl+Z)';
    undoButton.style.cssText = `
      width: 40px;
      height: 40px;
      background: white;
      border: 2px solid #ccc;
      border-radius: 4px;
      cursor: pointer;
      display: none;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      transition: all 0.2s;
    `;
    undoButton.addEventListener('mouseover', () => {
      undoButton.style.background = '#f5f5f5';
      undoButton.style.borderColor = '#4CAF50';
    });
    undoButton.addEventListener('mouseout', () => {
      undoButton.style.background = 'white';
      undoButton.style.borderColor = '#ccc';
    });
    undoButton.onclick = (e) => {
      e.stopPropagation();
      this.undoLastPolylinePoint();
    };

    // Create Cancel button
    const cancelButton = document.createElement('button');
    cancelButton.className = 'leaflet-control-button drawing-cancel-btn';
    cancelButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <line x1="18" y1="6" x2="6" y2="18" stroke-width="2" stroke-linecap="round"/>
        <line x1="6" y1="6" x2="18" y2="18" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    cancelButton.title = 'Cancel Drawing (Esc)';
    cancelButton.style.cssText = `
      width: 40px;
      height: 40px;
      background: white;
      border: 2px solid #ccc;
      border-radius: 4px;
      cursor: pointer;
      display: none;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      transition: all 0.2s;
    `;
    cancelButton.addEventListener('mouseover', () => {
      cancelButton.style.background = '#f5f5f5';
      cancelButton.style.borderColor = '#f44336';
    });
    cancelButton.addEventListener('mouseout', () => {
      cancelButton.style.background = 'white';
      cancelButton.style.borderColor = '#ccc';
    });
    cancelButton.onclick = (e) => {
      e.stopPropagation();
      this.cancelCurrentPolyline();
      this.disable();
    };

    // Add buttons to container
    controlContainer.appendChild(undoButton);
    controlContainer.appendChild(cancelButton);

    // Add container to map
    this.map.getContainer().appendChild(controlContainer);
    this.drawingControlsContainer = controlContainer;
  }

  /**
   * Remove drawing control buttons
   */
  private removeDrawingControls(): void {
    if (this.drawingControlsContainer && this.drawingControlsContainer.parentNode) {
      this.drawingControlsContainer.parentNode.removeChild(this.drawingControlsContainer);
      this.drawingControlsContainer = null;
    }
  }

  /**
   * Handle polyline click to add points
   */
  private handlePolylineClick(e: any): void {
    if (!this.polylineDrawingEnabled) return;
    (window as any).L.DomEvent.stopPropagation(e);

    if (this.currentPolylinePoints.length >= 3 && this.currentPolylinePoints[0]) {
      const firstPointPx = this.map.latLngToContainerPoint(this.currentPolylinePoints[0]);
      const cursorPx = this.map.latLngToContainerPoint(e.latlng);
      const isCloseToStart = firstPointPx.distanceTo(cursorPx) <= 12;

      if (isCloseToStart) {
        this.finishPolylineDrawing();
        this.disable();
        return;
      }
    }

    this.addPolylinePoint(e.latlng);
  }

  /**
   * Handle polyline right-click to undo last point
   */
  private handlePolylineRightClick(e: any): void {
    if (!this.polylineDrawingEnabled) return;
    e.preventDefault();
    this.undoLastPolylinePoint();
  }

  /**
   * Handle keyboard shortcuts during polyline drawing
   */
  private handlePolylineKeyDown(e: any): void {
    if (!this.polylineDrawingEnabled) return;

    if (e.key === 'Escape') {
      this.cancelCurrentPolyline();
      this.disable();
    }
    if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.undoLastPolylinePoint();
    }
    if (e.key === 'Enter' && this.currentPolylinePoints.length >= 3) {
      this.finishPolylineDrawing();
      this.disable();
    }
  }

  /**
   * Handle polyline mouse move for interactive preview
   */
  private handlePolylineMouseMove(e: any): void {
    if (!this.polylineDrawingEnabled || this.currentPolylinePoints.length === 0) {
      this.map.getContainer().style.cursor = 'crosshair';
      const firstMarker = this.currentPolylineMarkers[0];
      if (firstMarker) {
        firstMarker.setStyle({
          radius: 7,
          fillColor: '#2196F3',
          fillOpacity: 1,
          color: '#fff',
          weight: 2
        });
        firstMarker.closeTooltip();
      }
      return;
    }

    const canClosePolygon = this.currentPolylinePoints.length >= 3 && this.currentPolylinePoints[0];
    let shouldHighlightStart = false;

    if (canClosePolygon) {
      const firstPointPx = this.map.latLngToContainerPoint(this.currentPolylinePoints[0]);
      const cursorPx = this.map.latLngToContainerPoint(e.latlng);
      shouldHighlightStart = firstPointPx.distanceTo(cursorPx) <= 12;
    }

    this.map.getContainer().style.cursor = shouldHighlightStart ? 'pointer' : 'crosshair';
    const firstMarker = this.currentPolylineMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: shouldHighlightStart ? 10 : 7,
        fillColor: shouldHighlightStart ? '#FF9800' : '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: shouldHighlightStart ? 3 : 2
      });

      if (shouldHighlightStart) {
        firstMarker
          .bindTooltip(this.translateService.instant('DRAW_CLOSE_POLYGON_HINT'), {permanent: false, direction: 'top'})
          .openTooltip();
      } else {
        firstMarker.closeTooltip();
      }
    }

    const previewPoints = [...this.currentPolylinePoints, e.latlng];

    if (this.currentPolylinePoints.length >= 2) {
      if (this.currentPolyline) {
        this.map.removeLayer(this.currentPolyline);
      }

      this.currentPolyline = (window as any).L.polygon(previewPoints, {
        color: '#FF9800',
        weight: 3,
        opacity: 0.8,
        fill: true,
        fillColor: '#FF9800',
        fillOpacity: 0.3,
        dashArray: '5, 5',
        interactive: true,
        className: 'polygon-preview'
      }).addTo(this.map);

      this.currentPolyline.on('mouseover', () => {
        if (this.polylineDrawingEnabled && this.currentPolyline) {
          this.currentPolyline.setStyle({
            fillOpacity: 0.5,
            opacity: 1
          });
        }
      });

      this.currentPolyline.on('mouseout', () => {
        if (this.polylineDrawingEnabled && this.currentPolyline) {
          this.currentPolyline.setStyle({
            fillOpacity: 0.3,
            opacity: 0.8
          });
        }
      });

      // Update real-time measurements
      this.updateRealTimeMeasurements(previewPoints);
    } else if (this.currentPolylinePoints.length === 1) {
      // Show distance from first point to cursor
      if (this.currentPolyline) {
        this.map.removeLayer(this.currentPolyline);
      }

      this.currentPolyline = (window as any).L.polyline(previewPoints, {
        color: '#FF9800',
        weight: 3,
        opacity: 0.8,
        dashArray: '5, 5',
        interactive: false
      }).addTo(this.map);
    }
  }

  /**
   * Update real-time measurement labels during drawing
   */
  private updateRealTimeMeasurements(points: any[]): void {
    // Clear old measurement labels
    this.clearMeasurementLabels();

    // All measurement labels removed - only shape preview remains
  }

  /**
   * Update distance label for single point drawing
   */
  private updateDistanceLabel(point1: any, point2: any): void {
    // Clear old labels
    this.clearMeasurementLabels();

    // Distance label removed - only line preview remains
  }

  /**
   * Clear all measurement labels
   */
  private clearMeasurementLabels(): void {
    this.measurementLabels.forEach(label => {
      if (label && this.map.hasLayer(label)) {
        this.map.removeLayer(label);
      }
    });
    this.measurementLabels = [];
    this.areaLabel = null;
    this.perimeterLabel = null;
  }

  /**
   * Add a point to the current polyline
   */
  private addPolylinePoint(latlng: any): void {
    this.currentPolylinePoints.push(latlng);

    const isFirstPoint = this.currentPolylineMarkers.length === 0;
    const marker = (window as any).L.circleMarker(latlng, {
      radius: isFirstPoint ? 7 : 5,
      fillColor: isFirstPoint ? '#2196F3' : '#4CAF50',
      fillOpacity: 1,
      color: '#fff',
      weight: 2,
      interactive: true
    }).addTo(this.map);

    marker.bindTooltip(
      isFirstPoint
        ? this.translateService.instant('DRAW_CLOSE_POLYGON_HINT')
        : `Point ${this.currentPolylineMarkers.length + 1}`,
      {
      permanent: false,
      direction: 'top'
      }
    );

    this.currentPolylineMarkers.push(marker);
    this.map.getContainer().style.cursor = 'crosshair';
    const firstMarker = this.currentPolylineMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: 7,
        fillColor: '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: 2
      });
      firstMarker.closeTooltip();
    }

    if (this.currentPolylinePoints.length >= 2) {
      this.updatePolylineLines();
    }

    // Update measurements immediately after adding point
    if (this.currentPolylinePoints.length >= 3) {
      this.updateRealTimeMeasurements(this.currentPolylinePoints);
    }
  }

  /**
   * Update polyline visualization
   */
  private updatePolylineLines(): void {
    if (this.currentPolyline) {
      this.map.removeLayer(this.currentPolyline);
    }

    this.currentPolyline = (window as any).L.polyline(this.currentPolylinePoints, {
      color: '#4CAF50',
      weight: 3,
      opacity: 0.8,
      dashArray: '5, 5',
      interactive: false
    }).addTo(this.map);
  }

  /**
   * Finish polyline drawing and add to shapes
   */
  public finishPolylineDrawing(): void {
    if (this.currentPolylinePoints.length < 3) {
      this.cancelCurrentPolyline();
      return;
    }

    const finalPolygon = (window as any).L.polygon(this.currentPolylinePoints, {
      color: '#4CAF50',
      weight: 2,
      opacity: 0.9,
      fill: true,
      fillColor: '#4CAF50',
      fillOpacity: 0.4,
      interactive: true,
      className: 'drawn-polygon'
    }).addTo(this.map);

    // Calculate final area
    const areaHa = calculateAreaHa(this.currentPolylinePoints);

    this.shapeCreated$.next({
      type: 'polygon',
      coordinates: this.currentPolylinePoints.map(point => [point.lat, point.lng]),
      center: {
        lat: (this.currentPolylinePoints[0].lat + this.currentPolylinePoints[this.currentPolylinePoints.length - 1].lat) / 2,
        lng: (this.currentPolylinePoints[0].lng + this.currentPolylinePoints[this.currentPolylinePoints.length - 1].lng) / 2
      },
      layer: finalPolygon
    });

    finalPolygon.on('click', (e: any) => {
      console.log('Polygon clicked:', e);
    });

    if (this.drawnItems) {
      this.drawnItems.addLayer(finalPolygon);
    }

    this.completedPolygons.push(finalPolygon); // Track completed polygon

    this.cancelCurrentPolyline();
  }

  /**
   * Cancel current polyline drawing
   */
  private cancelCurrentPolyline(): void {
    this.map.getContainer().style.cursor = 'crosshair';
    const firstMarker = this.currentPolylineMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: 7,
        fillColor: '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: 2
      });
      firstMarker.closeTooltip();
    }

    this.currentPolylineMarkers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.currentPolylineMarkers = [];

    if (this.currentPolyline) {
      this.map.removeLayer(this.currentPolyline);
      this.currentPolyline = null;
    }

    // Clear measurement labels
    this.clearMeasurementLabels();

    this.currentPolylinePoints = [];
  }

  /**
   * Undo last polyline point
   */
  private undoLastPolylinePoint(): void {
    if (this.currentPolylinePoints.length === 0) return;

    this.currentPolylinePoints.pop();

    if (this.currentPolylineMarkers.length > 0) {
      const lastMarker = this.currentPolylineMarkers.pop();
      this.map.removeLayer(lastMarker);
    }

    if (this.currentPolylinePoints.length >= 2) {
      this.updatePolylineLines();
      if (this.currentPolylinePoints.length >= 3) {
        this.updateRealTimeMeasurements(this.currentPolylinePoints);
      }
    } else {
      if (this.currentPolyline) {
        this.map.removeLayer(this.currentPolyline);
        this.currentPolyline = null;
      }
      this.clearMeasurementLabels();
    }

    if (this.currentPolylinePoints.length < 3) {
      this.map.getContainer().style.cursor = 'crosshair';
      const firstMarker = this.currentPolylineMarkers[0];
      if (firstMarker) {
        firstMarker.setStyle({
          radius: 7,
          fillColor: '#2196F3',
          fillOpacity: 1,
          color: '#fff',
          weight: 2
        });
        firstMarker.closeTooltip();
      }
    }

  }

  /**
   * Get current polyline points
   */
  public getPolylinePoints(): any[] {
    return [...this.currentPolylinePoints];
  }

  /**
   * Clear all completed polygons from the map
   */
  private clearCompletedPolygons(): void {
    this.completedPolygons.forEach(polygon => {
      // Remove from drawnItems layer group first
      if (this.drawnItems && this.drawnItems.hasLayer(polygon)) {
        this.drawnItems.removeLayer(polygon);
      }
      // Then remove from map
      if (polygon && this.map.hasLayer(polygon)) {
        this.map.removeLayer(polygon);
      }
    });
    this.completedPolygons = [];
  }
}
